import { useCallback, useMemo, useState } from 'react';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import { ROUTE_DEFINITIONS } from '@/routes';
import { useCatalogLoader } from '@/hooks/ui/useCatalogLoader';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { useTranslation } from '@/i18n';
import { calculateDateRange } from '@/utils/dateRange';
import { downloadBlob } from '@/utils';
import {
  employeeDocumentService,
  type EmployeeDocument,
  type DocumentType,
} from '@/api/domains/employeeDocument';
import type { DateRangeValue } from '@/components/ui/filters/DateRangePicker';


export type MisDocumentosTab = 'documentos' | 'firmas';

type MisDocumentosFilters = {
  dateRange: DateRangeValue;
  selectedFolder: string | undefined;
  selectedDocumentType: string | undefined;
};

export interface UseMisDocumentosResult {
  // Tab
  activeTab: MisDocumentosTab;
  setActiveTab: (tab: MisDocumentosTab) => void;
  // Date filters
  dateRange: DateRangeValue;
  setDateRange: (range: DateRangeValue) => void;
  // Selector filters
  selectedFolder: string | undefined;
  setSelectedFolder: (value: string | undefined) => void;
  selectedDocumentType: string | undefined;
  setSelectedDocumentType: (value: string | undefined) => void;
  // Search
  onSearch: (text: string) => void;
  // Table
  columns: TableColumn<EmployeeDocument>[];
  actions: TableAction<EmployeeDocument>[];
  rows: EmployeeDocument[];
  total: number;
  loading: boolean;
  // Pagination
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  onSortChange: (key: string, direction: 'asc' | 'desc') => void;
  // Catalog options
  folderOptions: SelectorOption[];
  documentTypeOptions: SelectorOption[];
  isCatalogsLoaded: boolean;
}

export function useMisDocumentos(): UseMisDocumentosResult {
  const { t: translate } = useTranslation();
  const { showNotification, showApiError } = useUI();

  // --- Tab state ---
  const [activeTab, setActiveTabState] = useState<MisDocumentosTab>('documentos');
  const setActiveTab = useCallback((tab: MisDocumentosTab) => {
    setActiveTabState(tab);
  }, []);

  // --- Folder options derived from loaded documents ---
  // Documents carry the employee-specific folderId (e.g. 1489918) that the filter endpoint expects.
  // The /catalog/company-folders endpoint returns a different company-level template ID,
  // which does not match.
  const [knownFolders, setKnownFolders] = useState<Map<number, string>>(new Map());

  // --- Paginated list ---
  const initialFilters = useMemo<MisDocumentosFilters>(() => {
    const { dateFrom, dateTo } = calculateDateRange('MONTH_1');
    return {
      dateRange: { from: dateFrom, to: dateTo },
      selectedFolder: undefined,
      selectedDocumentType: undefined,
    };
  }, []);

  const fetchFn = useCallback(async (
    params: PaginatedFetchParams<MisDocumentosFilters>,
    _signal: AbortSignal,
  ): Promise<PaginatedFetchResult<EmployeeDocument> | null> => {
    const response = await employeeDocumentService.list({
      dateFrom: params.filters.dateRange.from ?? undefined,
      dateTo: params.filters.dateRange.to ?? undefined,
      folderId: params.filters.selectedFolder ? Number(params.filters.selectedFolder) : undefined,
      documentTypeId: params.filters.selectedDocumentType
        ? Number(params.filters.selectedDocumentType)
        : undefined,
      page: params.page,
      pageSize: params.pageSize,
      searchText: params.searchText || undefined,
      orderBy: params.sort?.key || undefined,
      orderDirection: params.sort?.direction,
    });

    if (!response.success || !response.data) {
      showApiError(response);
      return null;
    }

    // Accumulate known folders as a side-effect so the folder selector stays populated
    setKnownFolders(prev => {
      const next = new Map(prev);
      let changed = false;
      for (const doc of response.data!.data) {
        if (doc.folderId && !next.has(doc.folderId)) {
          next.set(doc.folderId, doc.folderName || '');
          changed = true;
        }
      }
      return changed ? next : prev;
    });

    return { items: response.data.data, totalRecords: response.data.totalRecords };
  }, [showApiError]);

  const {
    rows, total, page, pageSize, setPage, setPageSize,
    loading, onSearch, onSortChange, filters, setFilters,
  } = usePaginatedList<EmployeeDocument, MisDocumentosFilters>({
    fetchFn,
    initialFilters,
    screenCode: ROUTE_DEFINITIONS.CARPETA_DIGITAL.code,
  });

  // --- Filter wrappers (setFilters auto-resets page + pageSize to 1) ---
  const setDateRange = useCallback((range: DateRangeValue) => {
    setFilters(prev => ({ ...prev, dateRange: range }));
  }, [setFilters]);

  const setSelectedFolder = useCallback((value: string | undefined) => {
    setFilters(prev => ({ ...prev, selectedFolder: value }));
  }, [setFilters]);

  const setSelectedDocumentType = useCallback((value: string | undefined) => {
    setFilters(prev => ({ ...prev, selectedDocumentType: value }));
  }, [setFilters]);

  // --- Catalog: Document Types ---
  const allFoldersLabel = translate('employee:misDocumentos.filters.allOption');
  const documentTypeCatalog = useCatalogLoader<DocumentType>(
    (signal) => employeeDocumentService.getDocumentTypes(signal),
    (type) => ({ value: String(type.id), label: type.name }),
    [],
    false,
    () => showNotification('error', translate('common:errors.unexpected'), 5000),
  );

  const folderOptions: SelectorOption[] = useMemo(
    () => [
      { value: '', label: allFoldersLabel },
      ...Array.from(knownFolders.entries()).map(([id, name]) => (
        { value: String(id), label: name }
      )),
    ],
    [knownFolders, allFoldersLabel],
  );

  const documentTypeOptions: SelectorOption[] = useMemo(
    () => [{ value: '', label: allFoldersLabel }, ...documentTypeCatalog.options],
    [documentTypeCatalog.options, allFoldersLabel],
  );

  const isCatalogsLoaded = documentTypeCatalog.isLoaded;

  // --- Download handler ---
  const handleDownload = useCallback(async (row: EmployeeDocument) => {
    const response = await employeeDocumentService.download(row.id);
    if (response.success && response.data) {
      downloadBlob(response.data.blob, response.data.filename || row.documentName || `documento_${row.id}`);
    } else {
      showApiError(response, translate('employee:misDocumentos.messages.downloadError'));
    }
  }, [showApiError, translate]);

  // --- Table columns ---
  const columns: TableColumn<EmployeeDocument>[] = useMemo(
    () => [
      {
        key: 'folderName',
        header: translate('employee:misDocumentos.table.folder'),
        sortable: true,
      },
      {
        key: 'documentName',
        header: translate('employee:misDocumentos.table.documentName'),
        sortable: true,
      },
      {
        key: 'documentType',
        header: translate('employee:misDocumentos.table.type'),
        sortable: true,
      },
      {
        key: 'createdAt',
        header: translate('employee:misDocumentos.table.createdAt'),
        sortable: true,
      },
    ],
    [translate],
  );

  // --- Table actions ---
  const actions: TableAction<EmployeeDocument>[] = useMemo(
    () => [
      {
        id: 'download',
        label: translate('common:tableActions.download'),
        icon: 'import',
        onClick: (row) => { void handleDownload(row); },
      },
    ],
    [translate, handleDownload],
  );

  return {
    activeTab,
    setActiveTab,
    dateRange: filters.dateRange,
    setDateRange,
    selectedFolder: filters.selectedFolder,
    setSelectedFolder,
    selectedDocumentType: filters.selectedDocumentType,
    setSelectedDocumentType,
    onSearch,
    columns,
    actions,
    rows,
    total,
    loading,
    page,
    setPage,
    pageSize,
    setPageSize,
    onSortChange,
    folderOptions,
    documentTypeOptions,
    isCatalogsLoaded,
  };
}

export default useMisDocumentos;
